import { DEFAULT_PRICING_RULES, ruleValue } from '@repo/db/pricing-rules'

import type { DiscountCode } from '@repo/db/schema/discounts'

/**
 * Codes an Admin mints by hand (spec §14 "Kody rabatowe" · H1).
 *
 * The interesting rule is not the arithmetic, it is the EXCLUSIVITY: "Działają
 * tylko gdy nie ma innego aktywnego rabatu (wyjątek: kod minus 149 zł za
 * konsultację)." A code is an alternative to the ladders, not an addition to
 * them — which makes it the third member of a family spec §14 is consistent
 * about, alongside "rabat ilościowy i bonus za łączenie nie kumulują się".
 *
 * And the refusal has to be LOUD. A code that silently comes off nothing is the
 * worst of the three possible behaviours: the customer typed something, the
 * total did not move, and no screen said why. Every predicate here returns a
 * sentence rather than a boolean for that reason.
 *
 * Pure. The atomic claim — the only place a use limit is actually enforced —
 * lives in the query layer, because a limit checked here and written later is
 * two checkouts spending the last use of the same code.
 */

/**
 * Just enough of a basket to judge a code against it.
 *
 * TWO figures, because a code may be scoped to one service. `netMinor` is the
 * whole basket and is what spec §14's „minimalna wartość koszyka" is measured
 * against; `eligibleNetMinor` is the part the code is allowed to touch, and is
 * what the discount is actually computed from. For an unscoped code they are
 * the same number, which is why the second is optional and defaults to the
 * first — a caller that does not know about scopes cannot accidentally halve a
 * discount by omitting it.
 */
export interface CodeBasket {
  /** Net grosze BEFORE the code, after every ladder. */
  readonly netMinor: number
  /** Net grosze of the lines this code may discount. Defaults to `netMinor`. */
  readonly eligibleNetMinor?: number
  /** What the code is scoped to, named — for the refusal. */
  readonly scopeLabel?: string | null
}

/** The part of the basket a code may take from — the whole of it, by default. */
function eligibleOf(basket: CodeBasket): number {
  return basket.eligibleNetMinor ?? basket.netMinor
}

/**
 * The most any code may take off a basket — 99%, in basis points.
 *
 * ── WHY A BASKET MAY NEVER REACH ZERO ────────────────────────────────────
 * Because an order for nothing cannot be paid, and FLOU's whole fulfilment
 * hangs off the payment. A zeroed basket became an order sitting in
 * `AWAITING_PAYMENT` with nothing to pay: the gateway refuses a zero amount, so
 * no button appeared, nobody could settle it, and after seventy-two hours the
 * payment-window job marked it `FAILED`. The performer was never assigned and
 * earned nothing — which is the opposite of what a 100% code is for.
 *
 * The cap is on the AMOUNT taken, not on the percentage typed, and that is the
 * half worth noticing: a flat −500 zł code on a 300 zł basket zeroes it just as
 * surely as a 100% one. Both kinds go through here, so both are bounded by one
 * rule instead of by two that could drift.
 *
 * Owner's decision: "zróbmy 99% rabatu jako maksymalny próg".
 */
export const MAX_DISCOUNT_BPS = ruleValue(DEFAULT_PRICING_RULES, 'codes.max_discount_bps')

/**
 * What this code takes off a basket, NET grosze.
 *
 * Never more than {@link MAX_DISCOUNT_BPS} of the basket: a −149 zł code on an
 * 89 zł order takes 88,11 zł, not 149 and not 89. A negative total is something
 * no CHECK in this schema permits and no invoice can state; a zero one is
 * something nobody can pay, which turns out to be just as bad.
 *
 * Percent is floored: the house does not round a discount up against itself.
 */
export function codeDiscountMinor(
  code: Pick<DiscountCode, 'kind' | 'value'>,
  basketNetMinor: number,
  maxDiscountBps: number = MAX_DISCOUNT_BPS,
): number {
  if (basketNetMinor <= 0) return 0
  const raw =
    code.kind === 'PERCENT'
      ? Math.floor((basketNetMinor * code.value) / 10_000)
      : Math.trunc(code.value)
  // Floored, so the ceiling can never round UP into taking the whole basket.
  const ceiling = Math.floor((basketNetMinor * maxDiscountBps) / 10_000)
  return Math.max(0, Math.min(raw, ceiling))
}

/**
 * Why this code will not work here, in Polish, or `null` when it will.
 *
 * Seven refusals, in the order somebody would ask them: does it exist and is
 * it on, is it THEIRS, is it in season, is there any of it left, is the basket
 * big enough, and — last, because it is the one that is about the basket
 * rather than about the code — is there already a discount running.
 */
export function codeProblem(
  code: DiscountCode | null,
  basket: CodeBasket,
  now: Date,
  /** Who is typing it. Decides whether a PERSONAL code is theirs (spec §12 · I8). */
  userId: string | null = null,
): string | null {
  if (!code || !code.isActive) return 'Nie znamy takiego kodu. Sprawdź pisownię.'

  // A code minted FOR somebody is theirs alone (spec §12 · I8). Answered with
  // the same sentence as a code that does not exist, and deliberately: telling
  // a stranger "that code is real, just not yours" turns `KONSULTACJA-` —
  // which is short by design, because it gets read out loud — into something
  // worth guessing at.
  if (code.ownerUserId !== null && code.ownerUserId !== userId) {
    return 'Nie znamy takiego kodu. Sprawdź pisownię.'
  }

  if (code.validFrom && now.getTime() < code.validFrom.getTime()) {
    return 'Ten kod jeszcze nie działa.'
  }
  if (code.validUntil && now.getTime() >= code.validUntil.getTime()) {
    return 'Ten kod stracił ważność.'
  }
  if (code.maxUses !== null && code.usedCount >= code.maxUses) {
    return 'Ten kod został już wykorzystany.'
  }
  if (code.minBasketNetMinor !== null && basket.netMinor < code.minBasketNetMinor) {
    return `Ten kod działa od ${zloty(code.minBasketNetMinor)} netto w koszyku.`
  }

  // A code minted for ONE service, in a basket that has none of it. Refused
  // with the service NAMED, because „ten kod tu nie działa" sends somebody
  // checking their spelling of a code that is spelled correctly.
  if (code.offeringId !== null && eligibleOf(basket) <= 0) {
    return basket.scopeLabel
      ? `Ten kod działa tylko na: ${basket.scopeLabel}. W koszyku nie ma takiej pozycji.`
      : 'Ten kod działa tylko na jedną usługę, a w koszyku jej nie ma.'
  }

  // Spec §14's exclusivity test used to live here: a code was refused when the
  // basket had already earned a volume rung or the combining bonus, with the
  // consultation bonus as the one exception. There is nothing left for a code
  // to clash WITH — the ladders are gone and a code is the only discount this
  // shop applies — so the check went with them rather than standing as a
  // condition that can no longer be true.

  if (codeDiscountMinor(code, eligibleOf(basket)) <= 0) {
    return 'Ten kod nic tu nie zmienia — koszyk jest pusty albo nic nie kosztuje.'
  }
  return null
}

/** `14900` → `149 zł`. Whole złoty; every threshold in the spec is one. */
function zloty(minor: number): string {
  return `${(minor / 100).toLocaleString('pl-PL', { maximumFractionDigits: 2 })} zł`
}
